import { Controller } from '@hotwired/stimulus'
import Imask from 'imask'
import { stimulus } from '~/init'

class VehicleTransferController extends Controller {
  static targets = [
    'phoneField', 'hint', 'customerInfo', 'customerName', 'customerPhone', 'customerIdField',
    'newCustomerForm', 'firstNameField', 'lastNameField', 'companyNameField',
    'individualFields', 'companyFields', 'customerTypeToggle',
    'newCustomerPhoneField', 'newCustomerFirstNameField', 'newCustomerLastNameField', 'newCustomerCompanyNameField',
    'submitBtn', 'submitExisting', 'submitNew'
  ]

  static values = { searchUrl: String, textNotFound: String, prefillPhone: String }

  connect () {
    this.debounceTimer = null
    this.abortController = null

    this._initPhoneMask()

    if (this.prefillPhoneValue && this.prefillPhoneValue.replace(/\D/g, '').length >= 5) {
      this.setPhoneValue(this.prefillPhoneValue)
      this.searchCustomer(this._phoneDigits())
    }
  }

  disconnect () {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    if (this.abortController) this.abortController.abort()
    if (this._phoneFocusHandler && this.hasPhoneFieldTarget) {
      this.phoneFieldTarget.removeEventListener('focus', this._phoneFocusHandler)
    }
    if (this.phoneMask) this.phoneMask.destroy()
  }

  _initPhoneMask () {
    if (!this.hasPhoneFieldTarget) return

    const el = this.phoneFieldTarget

    this.phoneMask = Imask(el, {
      mask: '+{7} (000) 000-00-00',
      lazy: false,
      placeholderChar: '_',
      overwrite: 'shift'
    })

    if (!el.value || el.value.replace(/\D/g, '').length === 0) {
      this.phoneMask.value = '+7 (___) ___-__-__'
      this.phoneMask.updateValue()
    }

    this._phoneFocusHandler = () => {
      requestAnimationFrame(() => {
        const val = el.value
        const firstUnderscore = val.indexOf('_')
        if (firstUnderscore !== -1) {
          el.setSelectionRange(firstUnderscore, firstUnderscore)
        }
      })
    }
    el.addEventListener('focus', this._phoneFocusHandler)

    this.phoneMask.on('accept', () => this.onPhoneInput())
  }

  _phoneDigits () {
    return (this.phoneFieldTarget.value || '').replace(/\D/g, '')
  }

  setPhoneValue (phone) {
    if (!this.phoneMask) {
      this.phoneFieldTarget.value = phone
      return
    }

    let digits = (phone || '').replace(/\D/g, '')
    if (digits.startsWith('8') && digits.length === 11) {
      digits = '7' + digits.slice(1)
    }
    this.phoneMask.unmaskedValue = digits
  }

  onPhoneInput () {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)

    const digits = this._phoneDigits()
    if (digits.length < 5) {
      this.reset()
      return
    }

    this.debounceTimer = setTimeout(() => this.searchCustomer(digits), 400)
  }

  async searchCustomer (phone) {
    if (this.abortController) this.abortController.abort()
    this.abortController = new AbortController()

    this.hintTarget.textContent = '...'
    this.hintTarget.className = 'form__hint mt-1'

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ''
      const url = `${this.searchUrlValue}?phone=${encodeURIComponent(phone)}`
      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken }
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const data = await response.json()

      if (data.found) {
        this.customerNameTarget.textContent = data.customer.company_name || [data.customer.first_name, data.customer.last_name].filter(Boolean).join(' ')
        this.customerPhoneTarget.textContent = data.customer.phone
        this.customerIdFieldTarget.value = data.customer.id
        this.customerInfoTarget.classList.remove('hidden')
        this.newCustomerFormTarget.classList.add('hidden')
        this.submitExistingTarget.classList.remove('hidden')
        this.submitNewTarget.classList.add('hidden')
        this.submitBtnTarget.disabled = false
        this.hintTarget.textContent = ''
      } else {
        this.showNewCustomerForm(phone)
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        this.hintTarget.textContent = ''
      }
    }
  }

  showNewCustomerForm (phone) {
    this.customerInfoTarget.classList.add('hidden')
    this.customerIdFieldTarget.value = ''
    this.newCustomerPhoneFieldTarget.value = phone
    this.newCustomerFormTarget.classList.remove('hidden')
    this.submitExistingTarget.classList.add('hidden')
    this.submitNewTarget.classList.remove('hidden')
    this.hintTarget.textContent = this.textNotFoundValue
    this.hintTarget.className = 'form__hint mt-1 text-amber-600'
  }

  switchCustomerType (e) {
    const type = e.currentTarget.dataset.customerType
    const isCompany = type === 'company'

    this.customerTypeToggleTarget.querySelectorAll('button').forEach(btn => {
      const active = btn.dataset.customerType === type
      btn.classList.toggle('bg-background', active)
      btn.classList.toggle('shadow-sm', active)
      btn.classList.toggle('text-foreground', active)
      btn.classList.toggle('text-muted-foreground', !active)
    })

    this.individualFieldsTargets.forEach(el => el.classList.toggle('hidden', isCompany))
    this.companyFieldsTargets.forEach(el => el.classList.toggle('hidden', !isCompany))
  }

  reset () {
    this.customerInfoTarget.classList.add('hidden')
    this.customerIdFieldTarget.value = ''
    this.newCustomerFormTarget.classList.add('hidden')
    this.newCustomerPhoneFieldTarget.value = ''
    this.firstNameFieldTarget.value = ''
    this.lastNameFieldTarget.value = ''
    if (this.hasCompanyNameFieldTarget) this.companyNameFieldTarget.value = ''
    this.submitExistingTarget.classList.remove('hidden')
    this.submitNewTarget.classList.add('hidden')
    this.submitBtnTarget.disabled = true
    this.hintTarget.textContent = ''
  }

  // Sync visible fields to hidden inputs before submit
  syncNewCustomerFields () {
    this.newCustomerFirstNameFieldTarget.value = this.firstNameFieldTarget.value
    this.newCustomerLastNameFieldTarget.value = this.lastNameFieldTarget.value
    if (this.hasNewCustomerCompanyNameFieldTarget && this.hasCompanyNameFieldTarget) {
      this.newCustomerCompanyNameFieldTarget.value = this.companyNameFieldTarget.value
    }
  }
}

stimulus.register('vehicle-transfer', VehicleTransferController)
